import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { usePermissions } from "../../../../auth/PermissionProvider";
import { Permissions } from "../../../../auth/permissions";
import { Profile } from "../../../../interfaces/Profile";
import { createProfile, getAssignableProfiles } from "../../../../services/profileApi";

type Props = {
  id: string;
  /** Id do perfil escolhido, como texto ("" = nenhum). */
  value: string;
  onChange: (profileId: string, profileName?: string) => void;
  /** Nome da pessoa: vira "Perfil de <nome>" no perfil próprio. */
  personName: string;
  className: string;
};

/**
 * Select de UM perfil (a régua de acesso da pessoa) + "criar perfil próprio".
 * Perfil próprio é um perfil comum: copia a matriz do escolhido para um perfil novo com o nome da
 * pessoa; quem quiser ajustar a matriz vai para /admin/profiles.
 */
const ProfileSelect: React.FC<Props> = ({ id, value, onChange, personName, className }) => {
  const { can } = usePermissions();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdName, setCreatedName] = useState("");

  useEffect(() => {
    getAssignableProfiles()
      .then(setProfiles)
      .catch((e) => {
        console.error("Erro ao carregar perfis atribuíveis", e);
        setLoadError(true);
      });
  }, []);

  const selected = profiles.find((p) => String(p.id) === value);
  const canCreateOwn = can(Permissions.ProfilesManage) && !!selected && personName.trim().length > 0;

  const handleCreateOwn = async () => {
    if (!selected) return;
    setCreating(true);
    try {
      const created = await createProfile({
        name: `Perfil de ${personName.trim()}`,
        description: `Copiado de ${selected.name}`,
        permissions: selected.permissions,
      });
      setProfiles((list) => [...list, created]);
      onChange(String(created.id), created.name);
      setCreatedName(created.name);
    } catch (e: any) {
      console.error("Erro ao criar perfil próprio", e);
      toast.error(
        e?.response?.data?.message ??
          "Não foi possível criar o perfil próprio. Confira se já não existe um com esse nome."
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <select id={id} className={className} value={value} onChange={(e) => onChange(e.target.value, profiles.find((p) => String(p.id) === e.target.value)?.name)}>
        <option value="">Selecione um perfil</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      {loadError && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-300">
          Não foi possível carregar os perfis. Feche e abra de novo para tentar outra vez.
        </p>
      )}
      {canCreateOwn && (
        <button
          type="button"
          onClick={handleCreateOwn}
          disabled={creating}
          className="w-fit rounded-lg border border-dv-blue-200 px-2.5 py-1 text-[11px] font-semibold text-dv-blue-600 transition-all hover:bg-dv-blue-50 disabled:opacity-50 dark:border-dv-blue-500/30 dark:text-dv-cyan-300 dark:hover:bg-white/5"
        >
          {creating ? "Criando…" : "Criar perfil próprio"}
        </button>
      )}
      {createdName && (
        <p className="text-xs text-gray-700 dark:text-gray-300">
          {createdName} criado e selecionado.{" "}
          <Link to="/admin/profiles" className="font-semibold text-dv-blue-600 underline dark:text-dv-cyan-300">
            Ajustar a matriz em Perfis
          </Link>
        </p>
      )}
    </div>
  );
};

export default ProfileSelect;
